/**
 * ---------------------------------------------------------------------------
 * gn-default-size.js - Ghostnote Supply - 2026-09-22, scope extended 2026-09-25
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Every multi-size product page preselects the SMALLEST size, because Shopify
 * picks `product.selected_or_first_available_variant` and that is variant
 * position 1. The store has never sold an S: the only real orders have been
 * 2XL, L and M. On mobile the sticky Add to cart sits above the size row, so a
 * shopper can buy without ever seeing the selector.
 *
 * WHY IT IS JAVASCRIPT AND NOT LIQUID (both alternatives were tried, 2026-09-22)
 *
 * 1. Reordering variants so M sits at position 1 DOES preselect M, but it also
 *    reorders the size BUTTONS (S,M,L,XL,2XL became M,S,L,XL,2XL on
 *    hustle-hoodie). `ProductOptionValue` has no `position` field, so their
 *    display order is derived from variant order. That route is dead.
 *
 * 2. Overriding it in the three buy-box files does nothing. The checked radio
 *    is emitted by snippets/option-value.liquid line 142 from
 *    `option_value.selected`, which the PLATFORM computes from
 *    selected_or_first_available_variant. A theme cannot override it by passing
 *    a parameter. Doing it properly means editing option-value.liquid, which is
 *    shared by colour swatches, filter chips and product variations.
 *
 * So the size is selected the same way a shopper would select it, through the
 * theme's own variant machinery, which keeps the price, the gallery and the
 * hidden name="id" input consistent for free.
 *
 * SCOPE (2026-09-25)
 *
 * A. The MAIN page picker: the first <variant-picker> in the document at
 *    DOMContentLoaded, exactly as on 2026-09-22 (unchanged).
 * B. QUICK-BUY modals: the "Choose options" <quick-buy-modal> on product cards
 *    (/cart "While you decide", /search results, collection grids, product
 *    recommendations). Prestige fetches that content only when the modal is
 *    opened, long after DOMContentLoaded, so part A never saw it and every
 *    quick buy opened on S. Hook: Prestige's own `dialog:before-show` event,
 *    which QuickBuyModal.show() fires right after it inserts the fetched
 *    content (listened to in the capture phase because it does not bubble;
 *    `dialog:after-show` is a retry). No MutationObserver is needed. The same
 *    M selection runs on the modal's picker, once per opening, and never
 *    after the shopper has touched (pointer, key or change) that picker.
 *    Pickers the theme re-renders after an option change are never touched.
 *
 * WHAT IT DOES NOT DO
 *
 * - Nothing on the main picker when the URL carries ?variant=. Links from
 *   collections, ads, Klaviyo emails and Google must land on exactly the
 *   variant they name. (A quick-buy modal on such a page is a different
 *   product, so it still gets M.)
 * - Nothing when there is no Size option (beanies, caps), no "M" among the
 *   values, or the M control is disabled or already selected.
 * - Nothing on the 7 digital plugin products, which have no options.
 * - It changes the size only, never the colour the card or shopper chose.
 * - At most ONCE per main page load and once per quick-buy opening; it never
 *   fights a shopper's own choice.
 * - It never writes to the URL and never touches the canonical tag.
 * - Anything missing or throwing makes it a silent no-op.
 *
 * KNOWN COST
 *
 * The size row paints with S highlighted for a moment before M takes over.
 * There is no layout shift and, on this catalogue, no price or image change,
 * so the artifact is one button's outline moving.
 */
(function () {
  'use strict';

  try {
    if (!document.querySelector || !window.CustomEvent) {
      return;
    }

    // True only while this script dispatches its own change event, so the
    // quick-buy "shopper touched it" listener can ignore it.
    var dispatching = false;

    function labelFor(input, scope) {
      if (!input.id) {
        return null;
      }
      var esc = input.id;
      try {
        if (window.CSS && typeof CSS.escape === 'function') {
          esc = CSS.escape(input.id);
        }
        return (scope || document).querySelector('label[for="' + esc + '"]');
      } catch (selectorError) {
        return null;
      }
    }

    function isDisabled(input, label) {
      if (input.disabled) {
        return true;
      }
      var cls = label && label.className ? String(label.className) : '';
      return cls.indexOf('is-disabled') !== -1;
    }

    // Select M in one picker's Size group. Labels are looked up in `scope`.
    function selectM(picker, scope) {
      var groups = picker.querySelectorAll('fieldset');
      for (var g = 0; g < groups.length; g++) {
        var legend = groups[g].querySelector('legend');
        if (!legend) {
          continue;
        }
        // Legends read "Size:" / "Color:" and may carry a sr-only suffix.
        var name = legend.textContent.replace(/[\s:]+$/, '').trim().toLowerCase();
        if (name.indexOf('size') !== 0 && name.indexOf('taille') !== 0) {
          continue;
        }

        var radios = groups[g].querySelectorAll('input[type="radio"]');
        if (!radios.length) {
          return;
        }
        var current = groups[g].querySelector('input[type="radio"]:checked');

        for (var i = 0; i < radios.length; i++) {
          var radio = radios[i];
          var label = labelFor(radio, scope);
          var text = label ? label.textContent.trim() : '';
          if (text !== 'M') {
            continue;
          }
          if (radio === current) {
            return; // already M, nothing to do
          }
          if (isDisabled(radio, label)) {
            return; // M is not selectable in this colour: leave it alone
          }
          radio.checked = true;
          // Exactly what a tap produces, so the theme's own handler runs and
          // updates the price, the gallery and the hidden name="id" input.
          dispatching = true;
          try {
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          } finally {
            dispatching = false;
          }
          return;
        }
        return; // found the size group, it has no M
      }
    }

    // ---- A. Main page picker (behaviour unchanged since 2026-09-22) --------
    // A variant named in the URL is the shopper's intent. Never override it.
    if (window.location.search.indexOf('variant=') === -1) {
      var done = false;

      var run = function () {
        if (done) {
          return;
        }
        done = true;

        try {
          var picker = document.querySelector('variant-picker');
          if (!picker) {
            return; // single-variant product: nothing to select
          }
          selectM(picker, document);
        } catch (runError) {
          // Silent: the page keeps the theme's own default.
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    }

    // ---- B. Quick-buy modals ------------------------------------------------
    if (typeof WeakMap !== 'function' || typeof WeakSet !== 'function') {
      return;
    }
    var openings = new WeakMap(); // modal -> { done, touched } for the current opening
    var seenPickers = new WeakSet();

    function isQuickBuy(el) {
      return !!el && el.nodeType === 1 && el.tagName === 'QUICK-BUY-MODAL';
    }

    function applyTo(modal) {
      try {
        var st = openings.get(modal);
        if (!st || st.done || st.touched || !modal.hasAttribute('open')) {
          return;
        }
        var picker = modal.querySelector('variant-picker');
        if (!picker) {
          return; // content not there yet (after-show retries) or no options
        }
        st.done = true;
        if (seenPickers.has(picker)) {
          return;
        }
        seenPickers.add(picker);
        selectM(picker, modal);
      } catch (applyError) {
        // Silent: the modal keeps the theme's own default.
      }
    }

    function onShow(event) {
      try {
        var modal = event.target;
        if (!isQuickBuy(modal)) {
          return;
        }
        if (event.type === 'dialog:before-show' || !openings.has(modal)) {
          openings.set(modal, { done: false, touched: false }); // a fresh opening
        }
        // Let the theme finish show() (it may move the modal to <body>).
        setTimeout(function () {
          applyTo(modal);
        }, 0);
      } catch (showError) {
        // No-op.
      }
    }

    function onTouch(event) {
      try {
        if (dispatching) {
          return; // our own change event, not the shopper
        }
        var t = event.target;
        if (!t || !t.closest) {
          return;
        }
        var picker = t.closest('variant-picker');
        var modal = picker && picker.closest('quick-buy-modal');
        if (!modal) {
          return;
        }
        var st = openings.get(modal);
        if (st) {
          st.touched = true;
        }
      } catch (touchError) {
        // No-op.
      }
    }

    document.addEventListener('dialog:before-show', onShow, true);
    document.addEventListener('dialog:after-show', onShow, true);
    document.addEventListener('pointerdown', onTouch, true);
    document.addEventListener('keydown', onTouch, true);
    document.addEventListener('change', onTouch, true);
  } catch (gateError) {
    // No-op.
  }
})();
